import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { TrendingUp, DollarSign, Repeat, Users, Loader2, Receipt } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import PageHeader from "@/components/PageHeader";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const isPrivilegedRole = (r) => ["super_admin", "sysadmin", "owner"].includes(r);
const usd = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function StatCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="rounded-xl border border-line bg-bg-surface p-5" data-testid={testid}>
      <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-ink-muted mb-2">
        <Icon className="h-3.5 w-3.5 text-brand-amber" /> {label}
      </div>
      <div className="font-display text-2xl font-black tracking-tight text-ink-primary">{value}</div>
      {sub && <div className="text-xs text-ink-muted mt-1">{sub}</div>}
    </div>
  );
}

export default function AdminRevenue() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/admin/revenue")
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Failed to load revenue"));
  }, []);

  if (!user) return null;
  if (!isPrivilegedRole(user.role)) return <Navigate to="/dashboard" replace />;

  const delta = data && data.last_month > 0
    ? Math.round(((data.this_month - data.last_month) / data.last_month) * 100)
    : null;

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto animate-fade-up" data-testid="admin-revenue-page">
      <PageHeader
        eyebrow="// ADMIN"
        title="Revenue"
        description="Stripe + PayPal earnings, subscribers and MRR — your money at a glance."
      />

      {error && (
        <div className="rounded-md border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300" data-testid="revenue-error">{error}</div>
      )}
      {!data && !error && (
        <div className="flex items-center gap-2 py-16 justify-center text-ink-muted" data-testid="revenue-loading">
          <Loader2 className="h-5 w-5 animate-spin" /> Crunching the numbers…
        </div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard icon={DollarSign} label="Total revenue" value={usd(data.total_revenue)}
                      sub={`${data.payment_count} payment${data.payment_count === 1 ? "" : "s"} all-time`} testid="stat-total-revenue" />
            <StatCard icon={TrendingUp} label="This month" value={usd(data.this_month)}
                      sub={delta === null ? `Last month: ${usd(data.last_month)}` : `${delta >= 0 ? "▲" : "▼"} ${Math.abs(delta)}% vs last month`}
                      testid="stat-this-month" />
            <StatCard icon={Repeat} label="MRR" value={usd(data.mrr)} sub="Monthly recurring revenue" testid="stat-mrr" />
            <StatCard icon={Users} label="Paying subscribers" value={data.paying_subscribers}
                      sub={`${data.trialing_users} on trial`} testid="stat-subscribers" />
          </div>

          <div className="rounded-xl border border-line bg-bg-surface p-5 mb-8" data-testid="revenue-chart">
            <div className="flex items-center justify-between mb-4">
              <div className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">// LAST 12 MONTHS</div>
              <div className="text-xs text-ink-muted">
                Stripe {usd(data.by_provider?.stripe)} · PayPal {usd(data.by_provider?.paypal)}
              </div>
            </div>
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <BarChart data={data.monthly_series} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <XAxis dataKey="month" tick={{ fill: "#71717A", fontSize: 11 }} tickFormatter={(m) => m.slice(2)} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: "#71717A", fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v}`} width={54} />
                  <Tooltip
                    formatter={(v, name) => [usd(v), name === "stripe" ? "Stripe" : "PayPal"]}
                    contentStyle={{ background: "#14141B", border: "1px solid #27272A", borderRadius: 8, fontSize: 12 }}
                    labelStyle={{ color: "#FAFAFA" }}
                    cursor={{ fill: "rgba(255,138,31,0.06)" }}
                  />
                  <Legend formatter={(v) => <span style={{ color: "#A1A1AA", fontSize: 12 }}>{v === "stripe" ? "Stripe" : "PayPal"}</span>} />
                  <Bar dataKey="stripe" stackId="rev" fill="#FF8A1F" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="paypal" stackId="rev" fill="#22d3ee" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-4">
            <div className="rounded-xl border border-line bg-bg-surface p-5" data-testid="plan-breakdown">
              <div className="font-mono text-[10px] uppercase tracking-widest text-ink-muted mb-3">// SUBSCRIBERS BY PLAN</div>
              {Object.keys(data.plan_counts || {}).length === 0 ? (
                <p className="text-sm text-ink-muted">No paying subscribers yet.</p>
              ) : (
                <div className="space-y-2">
                  {Object.entries(data.plan_counts).map(([plan, count]) => (
                    <div key={plan} className="flex items-center justify-between text-sm">
                      <span className="text-ink-secondary capitalize">{plan.replace("_", " ")}</span>
                      <span className="font-semibold text-ink-primary">{count}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="lg:col-span-2 rounded-xl border border-line bg-bg-surface overflow-hidden" data-testid="recent-payments">
              <div className="font-mono text-[10px] uppercase tracking-widest text-ink-muted px-5 pt-5 pb-3">// RECENT PAYMENTS</div>
              {data.recent_payments.length === 0 ? (
                <div className="px-5 pb-8 text-center">
                  <Receipt className="h-8 w-8 mx-auto text-ink-muted mb-2" />
                  <p className="text-sm text-ink-muted">No payments yet — they'll appear here the moment money moves.</p>
                </div>
              ) : (
                <table className="w-full text-sm">
                  <tbody>
                    {data.recent_payments.map((p, i) => (
                      <tr key={i} className="border-t border-line/50">
                        <td className="px-5 py-2.5 text-ink-secondary whitespace-nowrap">
                          {p.date ? new Date(p.date).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—"}
                        </td>
                        <td className="px-3 py-2.5 text-ink-primary">{p.description}</td>
                        <td className="px-3 py-2.5 text-ink-secondary capitalize">{p.provider}</td>
                        <td className="px-5 py-2.5 text-right font-semibold whitespace-nowrap text-emerald-400">{usd(p.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
